/**
 * Generated by Claude - It just works and requires no manual intervention.
 * Verified by extensive testing
 */
import { styled } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { type CSSProperties, memo } from 'react';
import { cdnStaticPath } from '@/helpers/url';

const PRELOADER_SIZE_PX = 200;
const CANVAS_SIZE = 500;
const MASK_ID = 'qdrant-preloader-dots-mask';

// `qdrant.svg` is 70×80; the animation draws the mark at twice that size, centred on the canvas.
const LOGO_WIDTH = 140;
const LOGO_HEIGHT = 160;

// Outline of the mark as drawn above: the dots only show outside it.
const LOGO_SILHOUETTE = '249.8,169.3 180.9,209.3 180,290.7 250.7,330.2 276,316 292.9,325.3 319.6,311.1 319.1,210.2';

// Dots start from, and return to, just behind the mark's centre.
const DOT_ORIGIN = { x: 248.6, y: 251 };
const DOT_HEXAGON = '0,-1.5 -1.29,-0.75 -1.29,0.75 0,1.5 1.29,0.75 1.29,-0.75';
const DOT_COLORS = ['#038585', '#DC244C', '#2F6FF0', '#8547FF'] as const;
const DOT_COUNT = 98;
const DOT_MIN_RADIUS = 88;
const DOT_MAX_RADIUS = 212;
const DOT_STAGGER_STEPS = 5;
const DOT_STAGGER_MS = 125 / 3;

// Scatter the dots like the source does, but identically on every render: a seeded pseudo-random sequence
// (mulberry32) picks each dot's angle, ring position, colour and stagger independently. The ring position is
// mapped by area, so dots are no denser near the inner edge.
const DOT_SEED = 0x51d_c0de;
const nextRandom = (() => {
  let state = DOT_SEED;
  return () => {
    // Wrap to 32 bits, as the algorithm expects.
    state = (state + 0x6d_2b_79_f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 2 ** 32;
  };
})();
const pick = <T,>(items: readonly T[]) => items[Math.floor(nextRandom() * items.length)];

const DOTS = Array.from({ length: DOT_COUNT }, () => {
  const angle = nextRandom() * 2 * Math.PI;
  const radius = Math.sqrt(DOT_MIN_RADIUS ** 2 + nextRandom() * (DOT_MAX_RADIUS ** 2 - DOT_MIN_RADIUS ** 2));

  return {
    dx: radius * Math.cos(angle),
    dy: radius * Math.sin(angle),
    fill: pick(DOT_COLORS),
    delayMs: Math.floor(nextRandom() * DOT_STAGGER_STEPS) * DOT_STAGGER_MS,
  };
});

// Timings from the source animation (30 fps): dots fly out over 35 frames, hold until frame 77.5 and are back
// by frame 127.5; the dot field turns 60° every 197.5 frames. The source cuts the loop at frame 96, mid-return —
// the full cycle is used instead so the loop is seamless.
const SPREAD_CYCLE_MS = 4250;
const SPREAD_OUT_END = '27.45%';
const SPREAD_HOLD_END = '60.78%';
const SPREAD_EASING = 'cubic-bezier(0.588, 0, 0, 1)';
const FULL_TURN_MS = 39_500;

const spread = keyframes`
  0% {
    transform: translate(0, 0);
    animation-timing-function: ${SPREAD_EASING};
  }
  ${SPREAD_OUT_END} {
    transform: translate(var(--dx), var(--dy));
  }
  ${SPREAD_HOLD_END} {
    transform: translate(var(--dx), var(--dy));
    animation-timing-function: ${SPREAD_EASING};
  }
  100% {
    transform: translate(0, 0);
  }
`;

const turn = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

// Covers the app while it renders underneath. Outside the MUI theme, so the page background comes from the CSS
// variable `global.css` already paints the body with.
const StyledContainer = styled('div')({
  alignItems: 'center',
  backgroundColor: 'var(--mui-palette-background-default)',
  display: 'flex',
  inset: 0,
  justifyContent: 'center',
  position: 'fixed',
  zIndex: 9999,
});

// One animation rule for all dots, not a styled wrapper per dot.
const DotField = styled('g')({
  transformBox: 'view-box',
  transformOrigin: `${CANVAS_SIZE / 2}px ${CANVAS_SIZE / 2}px`,
  animation: `${turn} ${FULL_TURN_MS}ms linear infinite`,
  '& polygon': {
    animation: `${spread} ${SPREAD_CYCLE_MS}ms infinite both`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    '& polygon': { animation: 'none' },
  },
});

/**
 * Splash for a page refresh: the Qdrant mark with dots bursting out from behind it. Shown once by `AppRoot`, from auth
 * start-up until the first route resolves — never on in-app navigation.
 */
export const LoadingIndicator = memo(function LoadingIndicator() {
  return (
    <StyledContainer role="progressbar" aria-label="Loading">
      <svg
        viewBox={`0 0 ${CANVAS_SIZE} ${CANVAS_SIZE}`}
        width={PRELOADER_SIZE_PX}
        height={PRELOADER_SIZE_PX}
        aria-hidden={true}
      >
        <defs>
          {/* Inverted matte: the dots only show outside the mark's outline. */}
          <mask id={MASK_ID}>
            <rect width={CANVAS_SIZE} height={CANVAS_SIZE} fill="white" />
            <polygon points={LOGO_SILHOUETTE} fill="black" />
          </mask>
        </defs>

        <g mask={`url(#${MASK_ID})`}>
          <DotField>
            <g transform={`translate(${DOT_ORIGIN.x} ${DOT_ORIGIN.y})`}>
              {DOTS.map(({ dx, dy, fill, delayMs }, index) => (
                <polygon
                  key={index}
                  points={DOT_HEXAGON}
                  fill={fill}
                  style={{ '--dx': `${dx}px`, '--dy': `${dy}px`, animationDelay: `${delayMs}ms` } as CSSProperties}
                />
              ))}
            </g>
          </DotField>
        </g>

        <image
          href={cdnStaticPath('static/images/qdrant.svg')}
          x={(CANVAS_SIZE - LOGO_WIDTH) / 2}
          y={(CANVAS_SIZE - LOGO_HEIGHT) / 2}
          width={LOGO_WIDTH}
          height={LOGO_HEIGHT}
        />
      </svg>
    </StyledContainer>
  );
});
